import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Rating,
  Divider,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Assessment,
  Star,
  People,
  TrendingUp,
  Refresh
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getGeneralStats, getUniqueCourses, getEvaluationsByCourse } from '../services/evaluationService';

const COLORS = ['#FF6B6B', '#FFA726', '#FFD93D', '#6BCB77', '#4D96FF'];

function StatCard({ title, value, icon, color = 'primary' }) {
  return (
    <Card elevation={2}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, color: `${color}.main` }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.lighter`,
              borderRadius: 0,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function DashboardView() {
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('todos');
  const [courseEvaluations, setCourseEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [statsData, coursesData] = await Promise.all([
        getGeneralStats(),
        getUniqueCourses()
      ]);

      setStats(statsData);
      setCourses(coursesData);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const loadCourseData = async (courseName) => {
    if (courseName === 'todos') {
      setCourseEvaluations([]);
      return;
    }

    try {
      const evaluations = await getEvaluationsByCourse(courseName);
      setCourseEvaluations(evaluations);
    } catch (err) {
      console.error('Erro ao carregar avaliações do projeto:', err);
      // Optionally show an error message to the user
      setError('Erro ao carregar avaliações do projeto. Pode ser necessário criar um índice no Firestore.');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadCourseData(selectedCourse);
  }, [selectedCourse]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadData} sx={{ ml: 2 }}>
          Tentar Novamente
        </Button>
      </Alert>
    );
  }

  if (!stats) {
    return (
      <Alert severity="info">
        Nenhum dado disponível ainda.
      </Alert>
    );
  }

  // Prepara dados para os gráficos
  const ratingChartData = Object.entries(stats.ratingDistribution).map(([rating, count]) => ({
    rating: `${rating} estrela${rating > 1 ? 's' : ''}`,
    quantidade: count
  }));

  const courseChartData = Object.entries(stats.courseStats).map(([course, data]) => ({
    name: course.length > 20 ? course.substring(0, 20) + '...' : course,
    fullName: course,
    quantidade: data.count,
    media: parseFloat(data.average)
  }));

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Muito Insatisfeito',
      2: 'Insatisfeito',
      3: 'Regular',
      4: 'Satisfeito',
      5: 'Muito Satisfeito'
    };
    return labels[rating] || 'N/A';
  };

  const formatDate = (timestamp) => {
    try {
      if (!timestamp) return 'N/A';

      // Se for um Timestamp do Firestore
      if (timestamp.toDate) {
        return format(timestamp.toDate(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
      }

      // Se for uma string ISO
      if (typeof timestamp === 'string') {
        return format(new Date(timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
      }

      return 'N/A';
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return 'N/A';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Visão Geral das Avaliações
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
        >
          Atualizar
        </Button>
      </Box>

      {/* Cards de Estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total de Avaliações"
            value={stats.totalEvaluations}
            icon={<Assessment sx={{ color: 'primary.main', fontSize: 32 }} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Média Geral"
            value={stats.averageRating}
            icon={<Star sx={{ color: 'warning.main', fontSize: 32 }} />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Projetos"
            value={Object.keys(stats.courseStats).length}
            icon={<TrendingUp sx={{ color: 'success.main', fontSize: 32 }} />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Usuários"
            value={Object.keys(stats.userStats).length}
            icon={<People sx={{ color: 'info.main', fontSize: 32 }} />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Gráficos */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Distribuição de Notas
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ratingChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="rating" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="quantidade" fill="#2A78B7" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Proporção de Notas
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={ratingChartData}
                  dataKey="quantidade"
                  nameKey="rating"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {ratingChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Estatísticas por projeto */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Estatísticas por projeto
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Projeto</TableCell>
                <TableCell align="center">Avaliações</TableCell>
                <TableCell align="center">Média</TableCell>
                <TableCell align="center">Distribuição</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(stats.courseStats).map(([course, data]) => (
                <TableRow key={course} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {course}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={data.count} color="primary" size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Rating value={parseFloat(data.average)} precision={0.1} readOnly size="small" />
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {data.average}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {Object.entries(data.ratings).map(([rating, count]) => (
                        count > 0 && (
                          <Chip
                            key={rating}
                            label={`${rating}★: ${count}`}
                            size="small"
                            variant="outlined"
                          />
                        )
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Avaliações Recentes */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Avaliações Recentes
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filtrar por projeto</InputLabel>
            <Select
              value={selectedCourse}
              label="Filtrar por projeto"
              onChange={(e) => setSelectedCourse(e.target.value)}
            >
              <MenuItem value="todos">Todos os projetos</MenuItem>
              {courses.map((course) => (
                <MenuItem key={course} value={course}>
                  {course}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Projeto</TableCell>
                <TableCell>Usuário</TableCell>
                <TableCell align="center">Nota</TableCell>
                <TableCell>Comentário</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(selectedCourse === 'todos' ? stats.recentEvaluations : courseEvaluations).map((evaluation) => (
                <TableRow key={evaluation.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(evaluation.createdAt || evaluation.timestamp)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={evaluation.courseName || 'Sem projeto'}
                      size="small"
                      color={evaluation.courseName ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {evaluation.userId || 'anonimo'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Rating value={evaluation.rating} readOnly size="small" />
                      <Typography variant="caption" color="text.secondary">
                        {getRatingLabel(evaluation.rating)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {evaluation.comment || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {(selectedCourse === 'todos' ? stats.recentEvaluations : courseEvaluations).length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Nenhuma avaliação encontrada
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default DashboardView;
